"use client";

import { useState, useEffect, Fragment } from "react";
import { toast, ToastContainer } from "react-toastify";
import dynamic from "next/dynamic";
import { SwiperSlide } from "swiper/react";
import { fetchVideoBanner, generateJWTToken } from "@/utils/helpers/helperFunctions";
import BurnDistributionChart from "@/components/token-burn/BurnDistributionChart";
import BurnOverTimeChart from "@/components/token-burn/BurnOverTimeChart";
import BurnStatCards from "@/components/token-burn/BurnStatCards";
import RecentBurnEvents from "@/components/token-burn/RecentBurnEvents";
import SupplyBurnChart from "@/components/token-burn/SupplyBurnChart";
import NodeRewardsDailyBurnChart from "@/components/token-burn/NodeRewardsDailyBurnChart";
import NodeRewardsBurnProgress from "@/components/token-burn/NodeRewardsBurnProgress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const VideoBanner = dynamic(
  () => import("@/components/new/general/VideoBanner"),
  { ssr: false }
);

const LogoLoading = dynamic(() => import("@/components/ui/LogoLoading"));

const TokenBurnDashboard = () => {
  const [videoBannerURL, setVideoBannerURL] = useState(null);
  const [videoBannerMobileURL, setVideoBannerMobileURL] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Centralized data states
  const [burnStats, setBurnStats] = useState(null);
  const [burnEvents, setBurnEvents] = useState(null);
  const [dailyBurnData, setDailyBurnData] = useState(null);
  const [nodeRewardsStats, setNodeRewardsStats] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchVideoBanner("burn", setVideoBannerURL, setVideoBannerMobileURL);
    fetchAllBurnData();
    // Simulate loading time
    setTimeout(() => setLoading(false), 1000);
  }, []);

  // Extract the most recent date from burn events
  useEffect(() => {
    if (burnEvents && burnEvents.length > 0) {
      // Sort events by datetime, newest first
      const sortedEvents = [...burnEvents].sort((a, b) => {
        return new Date(b.datetime) - new Date(a.datetime);
      });
      
      // Set the most recent event's datetime
      if (sortedEvents[0]?.datetime) {
        setLastUpdated(sortedEvents[0].datetime);
      }
    }
  }, [burnEvents]);

  const fetchAllBurnData = async () => {
    try {
      setDataLoading(true);
      const token = await generateJWTToken();
      
      // Fetch APIs in parallel
      const [statsResponse, eventsResponse, nodeRewardsSummaryResponse] = await Promise.all([
        fetch(`https://cqfjxolodeqrlvaifybm.supabase.co/functions/v1/get-token-burn-stats`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`https://cqfjxolodeqrlvaifybm.supabase.co/functions/v1/get-token-burn-events`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        // Get node rewards daily burn summary data
        fetch(`https://cqfjxolodeqrlvaifybm.supabase.co/functions/v1/get-node-rewards-daily-burn-summary`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      const [statsData, eventsData, nodeRewardsSummaryData] = await Promise.all([
        statsResponse.json(),
        eventsResponse.json(),
        nodeRewardsSummaryResponse.json()
      ]);

      if (statsData.success && statsData.data && statsData.data.length > 0) {
        setBurnStats(statsData.data[0]);
      }

      if (eventsData.success && eventsData.data) {
        setBurnEvents(eventsData.data);
      }

      // Process daily summary data for chart (reverse to get chronological order since API returns newest first)
      if (nodeRewardsSummaryData.success && nodeRewardsSummaryData.data?.records) {
        const sortedData = [...nodeRewardsSummaryData.data.records].reverse();
        setDailyBurnData(sortedData);
      }

      // Calculate node rewards stats
      if (nodeRewardsSummaryData.success && nodeRewardsSummaryData.data && statsData.data?.[0]) {
        // Use the total_burned_amount from API response (equivalent to summing all records)
        const totalCandidateAmount = parseFloat(nodeRewardsSummaryData.data.total_burned_amount || 0);
        const totalBurnedFromEvents = parseFloat(statsData.data[0].total_burn_amt_node_rewards || 0);
        
        setNodeRewardsStats({
          total_burned_from_events: totalBurnedFromEvents, // Green - already burned
          total_candidate_amount: totalCandidateAmount      // Red - pending candidates
        });
      }
    } catch (error) {
      console.error("Error fetching burn data:", error);
      toast.error("Failed to load burn data");
    } finally {
      setDataLoading(false);
    }
  };

  return (
    <Fragment>
      <VideoBanner>
        <SwiperSlide>
          <div
            className={`swiperSlideDiv relative w-full h-[300px] md:h-[250px] transition-transform duration-[600ms] ease-in-out`}
          >
            <video
              preload="auto"
              src={
                videoBannerURL ||
                "https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/website%2Fpage_chain%2Fbanner_chain_no_title.mp4?alt=media&token=db3d8123-5045-4690-81f5-5140e72c679a"
              }
              autoPlay
              loop
              muted
              playsInline
              className="object-cover w-full h-full hidden md:block"
            />

            <video
              preload="auto"
              src={
                videoBannerMobileURL ||
                "https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/website%2Fpage_chain%2Fbanner_chain_no_title.mp4?alt=media&token=db3d8123-5045-4690-81f5-5140e72c679a"
              }
              autoPlay
              loop
              muted
              playsInline
              className="object-cover w-full h-full md:hidden"
            />

            <div className="absolute !pr-8 !pl-8 xl:container inset-x-[8%] 2xl:inset-x-[0%] block pt-2 pb-2 md:pb-4 text-left text-white top-1/2 transform -translate-y-1/2">
              <h1 className="text-xl md:text-2xl font-extrabold bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent mb-1 tracking-tight">
                Token Burn Statistics
              </h1>
              <p className="text-xs md:text-sm text-white max-w-xl">
                Track MXS token burning activities, supply reduction metrics, and ecosystem burn distribution with real-time analytics and comprehensive visualizations.
              </p>
            </div>
          </div>
        </SwiperSlide>
      </VideoBanner>

      <div className="bg-metaxseed-dark-gray">
        <div className="max-w-7xl mx-auto md:px-8 px-4 py-6">
          <ToastContainer position="top-right" autoClose={5000} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable theme="dark" />
          
          {loading || dataLoading ? (
            <div className="flex justify-center items-center py-12">
              <LogoLoading />
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <BurnStatCards data={burnStats} nodeRewardsStats={nodeRewardsStats} lastUpdated={lastUpdated} />
              </div>
              
              <Tabs defaultValue="overview" className="space-y-4">
                <TabsList className="bg-[#161a23] border border-[#2a2f38]">
                  <TabsTrigger value="overview" className="data-[state=active]:bg-metaxseed-blue data-[state=active]:text-white">
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="analytics" className="data-[state=active]:bg-metaxseed-blue data-[state=active]:text-white">
                    Analytics
                  </TabsTrigger>
                  <TabsTrigger value="events" className="data-[state=active]:bg-metaxseed-blue data-[state=active]:text-white">
                    Events
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="overview" className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
                    <div className="col-span-4">
                      <BurnOverTimeChart data={burnEvents} detailed />
                    </div>
                    <div className="col-span-3">
                      <BurnDistributionChart data={burnStats} />
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
                    <div className="col-span-4">
                      <RecentBurnEvents data={burnEvents} />
                    </div>
                    <div className="col-span-3">
                      <SupplyBurnChart data={burnStats} />
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
                    <div className="col-span-4">
                      <NodeRewardsDailyBurnChart data={dailyBurnData} />
                    </div>
                    <div className="col-span-3">
                      <NodeRewardsBurnProgress nodeRewardsStats={nodeRewardsStats} />
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="analytics" className="space-y-4">
                  {/* Charts at the top */}
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <div className="col-span-1">
                      <BurnDistributionChart data={burnStats} />
                    </div>
                    <div className="col-span-1">
                      <SupplyBurnChart data={burnStats} />
                    </div>
                    <div className="col-span-1">
                      <NodeRewardsBurnProgress nodeRewardsStats={nodeRewardsStats} />
                    </div>
                  </div>
                  {/* Graphs at the bottom */}
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
                    <div className="col-span-7 lg:col-span-4">
                      <BurnOverTimeChart data={burnEvents} detailed />
                    </div>
                    <div className="col-span-7 lg:col-span-3">
                      <NodeRewardsDailyBurnChart data={dailyBurnData} />
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="events" className="space-y-4">
                  <RecentBurnEvents data={burnEvents} fullTable />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default TokenBurnDashboard; 